import { useRouter } from 'next/navigation';
import React, { useMemo } from 'react';

import { trackGAEvent } from '@/utility/analytics';
import { HOMETAB_EVENTS } from '@/utility/analyticsConsts';
import config from '@/utility/config';
import {
  formatNumber,
  getNumberWithoutNegativeSign
} from '@/utility/helpers';
import { formatDateTimeWithLocale, t } from '@/utility/localization';
import {
  CM_PORTAL_MEMBERS_PAGE_ROUTE,
  CM_PORTAL_MONEY_PAGE_ROUTE
} from '@/utility/routesHelper';

import Icon from '@/components/npl/Icon';
import NPLButton from '@/components/npl/NPLButton';

import { SummarycardContainer } from '../../money/components/SummaryCardContainer';
import { CommunityVisitsChart } from '../components/CommunityVisitsChart';
import getConstants from '../constants';
import useSummaryCardSection from '../hooks/useSummaryCardSection';

function SummaryCardSection({ activeCommunity }) {
  const router = useRouter();
  const { EARNINGS_STATES } = getConstants();

  const {
    earningsData,
    fetchingAllData,
    visitsData,
    memberCountData,
    earningsCardState
  } = useSummaryCardSection({
    activeCommunity
  });

  // VARIABLES FOR EARNINGS CARD
  const currency = earningsData?.currency ?? activeCommunity?.baseCurrency;

  const totalAmountEarnedThisMonth = formatNumber(
    earningsData?.overallEarnings?.total?.amount,
    true,
    true
  );

  const totalSalesThisMonth =
    earningsData?.overallEarnings?.total?.quantity || '0';

  const hasEarnings = earningsData?.earningExists;

  const currentDate = new Date();

  const routeToMoneyPage = () => {
    trackGAEvent(HOMETAB_EVENTS.EARNINGS_CARD_CLICK);
    router.push(CM_PORTAL_MONEY_PAGE_ROUTE);
  };

  const routeToMembersTabWithFilter = () => {
    trackGAEvent(HOMETAB_EVENTS.MEMBERS_CARD_CLICK);

    const queryParams = {
      segment: 'members',
      status: 'subscribed,not_on_nasio',
      activeCommunityId: activeCommunity?._id
    };

    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${CM_PORTAL_MEMBERS_PAGE_ROUTE}?${queryString}`;

    router.push(url);
  };

  const goToAnalyticsPage = () => {
    trackGAEvent(HOMETAB_EVENTS.COMMUNITY_VISITS_CARD_CLICK);
    router.push('/portal/analytics');
  };

  const renderChangeInMembersIcon = () => {
    const memberChange30Days = memberCountData?.last30Days;
    if (memberChange30Days > 0) {
      return (
        <Icon name="arrow-up" fill="#30A66D" width={16} height={16} />
      );
    } else if (memberChange30Days < 0) {
      return (
        <Icon name="arrow-down" fill="#E5484D" width={16} height={16} />
      );
    } else {
      // This will cover the case where memberChange30Days is 0 or undefined
      return (
        <Icon name="arrow-up" fill="#1B1B18A6" width={16} height={16} />
      );
    }
  };

  const isTrafficWhitelisted = useMemo(() => {
    if (!config.trafficWhitelistEnabled) return true;

    return config.trafficWhitelistIDs.includes(activeCommunity?._id);
  }, [activeCommunity?._id]);

  return (
    <div className="mt-24 grid grid-cols-1 gap-12 md:grid-cols-2 lg:flex">
      {/* Community visits card */}
      {isTrafficWhitelisted && (
        <div className="flex-1">
          <SummarycardContainer
            eyebrowIcon="bar-chart-circle-02"
            eyebrow={t('community-visits')}
            title={formatNumber(memberCountData?.count)}
            tooltip={t('total-visits-to-your-community-per-month')}
            isLoading={fetchingAllData}
            cardOnClick={goToAnalyticsPage}>
            <div className="absolute bottom-0 left-0 right-0">
              <CommunityVisitsChart
                visitsPerDay={
                  visitsData?.graphData?.map(
                    (data) => data.totalMetricValue
                  ) ?? []
                }
              />
            </div>
          </SummarycardContainer>
        </div>
      )}
      {/* Members Count Card */}
      <div className="flex-1">
        <SummarycardContainer
          eyebrowIcon="users-01"
          eyebrow={t('total-members')}
          title={formatNumber(memberCountData?.count)}
          tooltip={t('members-currently-in-your-community')}
          isLoading={fetchingAllData}
          cardOnClick={routeToMembersTabWithFilter}>
          {/* TODO include change in member count */}
          <div className="flex items-center gap-8">
            {renderChangeInMembersIcon()}
            <div className="text-label-md font-medium text-npl-text-icon-on-light-surface-tertiary">
              {t('in-the-last-30-days', {
                memberCount: getNumberWithoutNegativeSign(
                  memberCountData?.last30Days
                )
              })}
            </div>
          </div>
        </SummarycardContainer>
      </div>

      {/* Earnings Card */}
      <div className="flex-1">
        <SummarycardContainer
          eyebrowIcon="currency-dollar-circle"
          eyebrow={t('month-earnings', {
            month: formatDateTimeWithLocale(currentDate, 'MMMM')
          })}
          title={
            hasEarnings ? `${currency} ${totalAmountEarnedThisMonth}` : ''
          }
          tooltip={t(
            'the-sum-of-all-sales-and-subscriptions-for-the-month-prior-to-fee-deductions-and-upcoming-subscription-earnings'
          )}
          isLoading={fetchingAllData}
          cardOnClick={
            earningsCardState === EARNINGS_STATES.HAVE_PAID_COMMU_OR_ENTITY
              ? routeToMoneyPage
              : null
          }>
          <div className="flex flex-col gap-12">
            {earningsCardState ===
              EARNINGS_STATES.NO_PAID_COMM_AND_ENTITY && (
              <div>
                <NPLButton
                  hierarchy="neutral_primary"
                  buttonText={t('make-your-first-sale')}
                  onClick={routeToMoneyPage}
                  size="sm"
                />
              </div>
            )}

            <div className="text-label-md font-medium text-npl-text-icon-on-light-surface-tertiary">
              {formatNumber(totalSalesThisMonth)} {t('sales')}
            </div>
          </div>
        </SummarycardContainer>
      </div>
    </div>
  );
}

export default SummaryCardSection;
